import React, { useId } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { PaymentTone, Scenario } from '../../types/quote';
import { formatCurrency } from '../../utils/currency';
import { easeOut } from '../../utils/motion';
type PaymentBreakdownProps = {
  scenario: Scenario;
};
const toneClass: Record<PaymentTone, string> = {
  navy: 'bg-navy',
  flame: 'bg-flame',
  flameSoft: 'bg-flame-200',
  navySoft: 'bg-navy-400'
};
export function PaymentBreakdown({
  scenario
}: PaymentBreakdownProps) {
  const reduce = useReducedMotion();
  const headingId = useId();
  const barLabel = scenario.payment.map(item => `${item.label} ${formatCurrency(item.amount, true)}`).join(', ');
  return <section aria-labelledby={headingId} data-id="element-95" data-mp="t04cfrvt4o0o">
      <h4 id={headingId} className="text-base font-extrabold" data-id="element-96" data-mp="t3jpyq9u1ozz">
        Estimated monthly payment
      </h4>

      <div className="mt-3 flex h-4 w-full gap-1" role="img" aria-label={`Monthly payment breakdown: ${barLabel}`} data-id="element-97" data-mp="3dreh5h7vr4kw">
        {scenario.payment.map((item, index) => <motion.span key={item.label} className={`block h-full min-w-[12px] rounded-full ${toneClass[item.tone]}`} style={{
        flex: `${item.amount} 1 0%`,
        originX: 0
      }} initial={reduce ? false : {
        scaleX: 0,
        opacity: 0
      }} animate={{
        scaleX: 1,
        opacity: 1
      }} transition={{
        duration: 0.28,
        ease: easeOut,
        delay: 0.15 + index * 0.05
      }} data-id="element-98" data-mp="1ymtsxovq0oiq" data-mpi={index} />)}
      </div>

      <ul className="mt-4 divide-y divide-dashed divide-navy-100" data-id="element-99" data-mp="1jojajh90annn">
        {scenario.payment.map(item => <li key={item.label} className="flex items-center justify-between gap-3 py-2.5" data-id="element-100" data-mp="15xwlyhwyme5a">
            <span className="flex min-w-0 items-center gap-2.5" data-id="element-101" data-mp="1aw7verjcemmz">
              <span className={`h-3 w-3 shrink-0 rounded-full ${toneClass[item.tone]}`} aria-hidden="true" data-id="element-102" data-mp="1audcfirmt4lj" />
              <span className="min-w-0" data-id="element-103" data-mp="w1jkgxv6avzz">
                <span className="block text-sm font-bold" data-id="element-104" data-mp="10yjqj8s0vyr3">{item.label}</span>
                {item.note && <span className="block text-xs font-semibold text-navy-500" data-id="element-105" data-mp="hu6p4mng9ywg">{item.note}</span>}
              </span>
            </span>
            <span className="shrink-0 text-sm font-extrabold tabular-nums" data-id="element-106" data-mp="d1dp2c2xfeqp">{formatCurrency(item.amount, true)}</span>
          </li>)}
      </ul>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-[22px] bg-navy px-5 py-4 text-white" data-id="element-107" data-mp="350wjhj4b6xj">
        <span className="text-sm font-bold text-white/80" data-id="element-108" data-mp="10r7zyo3oqt8b">Estimated monthly payment</span>
        <span className="text-2xl font-black tabular-nums" data-id="element-109" data-mp="vuhjw0rvrxmw">{formatCurrency(scenario.paymentTotal, true)}</span>
      </div>
    </section>;
}